
import { ForgotPasswordComponent } from './page/customer/forgot-password/forgot-password.component';
import { ServiceConfirmationComponent } from './page/booking/service-confirmation/service-confirmation.component';
import { LoginComponent } from './page/customer/login/login.component';
import { SelectPractitionerComponent } from './page/booking/select-practitioner/select-practitioner.component';
import { BrowserModule } from '@angular/platform-browser';
import { LOCALE_ID, NgModule } from '@angular/core';

import { AppComponent } from './app.component';
import { HeaderComponent } from './layout/header/header.component';
import { FooterComponent } from './layout/footer/footer.component';
import { AppRoutingModule } from './app.routes';
import { MainComponent } from './layout/main/main.component';
import { SelectConsultationComponent } from './page/booking/select-consultation/select-consultation.component';
import { BaseComponent } from './layout/main/base.component';

import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, FormBuilder, ReactiveFormsModule } from '@angular/forms';

import { RegisterComponent } from './page/customer/register/register.component';

import { ThankYouComponent } from './page/booking/thank-you/thank-you.component';
import { TermsAndConditionsComponent } from './page/customer/terms-and-conditions/terms-and-conditions.component';

import { APP_INITIALIZER } from '@angular/core';
import { ConfigService } from './services/config.service';
import { StorageService } from './services/storage.service';

import { MyDatePickerModule } from 'mydatepicker';
import { ToastrModule } from 'ngx-toastr';
import { HttpClientModule } from '@angular/common/http';
import { SelectDateAndTimeComponent } from './page/booking/select-date-and-time/select-date-and-time.component';
import { WebBookingService } from './api/services/web-booking.service';

import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { ModalModule } from 'ngx-bootstrap/modal';
import { defineLocale } from 'ngx-bootstrap/chronos';

import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { InputsModule } from '@progress/kendo-angular-inputs';

import {
  arLocale, bgLocale, caLocale, csLocale, daLocale, deLocale, enGbLocale, esDoLocale,
  esLocale, esUsLocale, etLocale, fiLocale, frLocale, glLocale, heLocale, hiLocale, huLocale,
  idLocale, itLocale, jaLocale, koLocale, ltLocale, mnLocale, nbLocale, nlBeLocale, nlLocale,
  plLocale, ptBrLocale, roLocale, ruLocale, skLocale, slLocale, svLocale, thLocale, trLocale,
  viLocale, zhCnLocale
} from 'ngx-bootstrap/locale';

import { NgxSpinnerModule } from 'ngx-spinner';
import { ResetPasswordComponent } from './page/customer/reset-password/reset-password.component';
import { VerifyEmailAddressComponent } from './page/customer/verify-email-address/verify-email-address.component';
import { MigrateCustomerComponent } from './page/customer/migrate-customer/migrate-customer.component';
import { InvalidComponent } from './page/invalid/invalid.component';
import { ChangePasswordComponent } from './page/customer/change-password/change-password.component';
import { ProfileComponent } from './page/customer/profile/profile.component';
import { LogoutComponent } from './page/customer/logout/logout.component';

defineLocale('ar', arLocale);
defineLocale('bg', bgLocale);
defineLocale('ca', caLocale);
defineLocale('cs', csLocale);
defineLocale('da', daLocale);
defineLocale('de', deLocale);
defineLocale('en-gb', enGbLocale);
defineLocale('es', esDoLocale);
defineLocale('es', esLocale);
defineLocale('es-us', esUsLocale);
defineLocale('et', etLocale);
defineLocale('fi', fiLocale);
defineLocale('fr', frLocale);
defineLocale('gl', glLocale);
defineLocale('he', heLocale);
defineLocale('hi', hiLocale);
defineLocale('hu', huLocale);
defineLocale('id', idLocale);
defineLocale('it', itLocale);
defineLocale('ja', jaLocale);
defineLocale('ko', koLocale);
defineLocale('lt', ltLocale);
defineLocale('mn', mnLocale);
defineLocale('nb', nbLocale);
defineLocale('nl-be', nlBeLocale);
defineLocale('nl', nlLocale);
defineLocale('pl', plLocale);
defineLocale('pt', ptBrLocale);
defineLocale('ro', roLocale);
defineLocale('ru', ruLocale);
defineLocale('sk', skLocale);
defineLocale('sl', slLocale);
defineLocale('sv', svLocale);
defineLocale('th', thLocale);
defineLocale('tr', trLocale);
defineLocale('vi', viLocale);
defineLocale('zh-cn', zhCnLocale);

import { MatToolbarModule } from '@angular/material/toolbar';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatMomentDateModule } from '@angular/material-moment-adapter';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatMenuModule } from '@angular/material/menu';

import { CovidComponent } from './page/covid/covid.component';
import { StepsComponent } from './layout/steps/steps.component';
import { DatePipe } from '@angular/common';
import { VisitorGuard } from './guards/visitor.guard';
import { CustomerGuard } from './guards/customer.guard';
import { GiftVoucherRedemptionComponent } from './page/booking/gift-voucher-redemption/gift-voucher-redemption.component';
import { StringService } from './services/string.service';
import { GiftVoucherCartComponent } from './page/gift-voucher/gift-voucher-cart/gift-voucher-cart.component';
import { GiftVoucherCheckoutComponent } from './page/gift-voucher/gift-voucher-checkout/gift-voucher-checkout.component';
import { GiftVoucherDetailsComponent } from './page/gift-voucher/gift-voucher-details/gift-voucher-details.component';
import { GiftVoucherHomeComponent } from './page/gift-voucher/gift-voucher-home/gift-voucher-home.component';
import { GiftVoucherThankYouComponent } from './page/gift-voucher/gift-voucher-thank-you/gift-voucher-thank-you.component';
import { WebGiftVoucherService } from './api/services/web-gift-voucher.service';
import { NavigatorService } from './services/navigator.service';
import { SafeurlPipe } from './services/safeurl.pipe';
import { IncomingBookingComponent } from './page/customer/incoming-booking/incoming-booking.component';
import { TransactionHistoryComponent } from './page/customer/transaction-history/transaction-history.component';
import { DateService } from './services/date.service';
import { RescheduleComponent } from './page/customer/reschedule/reschedule.component';
import { CancelAppointmentComponent } from './page/customer/cancel-appointment/cancel-appointment.component';
import { ConfirmationDialogComponent } from './controls/confirmation-dialog/confirmation-dialog.component';
import { CancelDialogComponent } from './controls/cancel-dialog/cancel-dialog.component';
import { UpdateEmailAddressComponent } from './page/customer/update-email-address/update-email-address.component';
import { PasswordDialogComponent } from './controls/password-dialog/password-dialog.component';

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    MainComponent,
    BaseComponent,

    SelectConsultationComponent,
    SelectPractitionerComponent,
    ServiceConfirmationComponent,
    ThankYouComponent,

    LoginComponent,
    RegisterComponent,
    ForgotPasswordComponent,
    TermsAndConditionsComponent,
    SelectDateAndTimeComponent,
    ResetPasswordComponent,
    VerifyEmailAddressComponent,
    MigrateCustomerComponent,
    InvalidComponent,
    ChangePasswordComponent,
    ProfileComponent,
    LogoutComponent,
    CovidComponent,
    StepsComponent,
    GiftVoucherRedemptionComponent,
    GiftVoucherHomeComponent,
    GiftVoucherCartComponent,
    GiftVoucherCheckoutComponent,
    GiftVoucherThankYouComponent,
    GiftVoucherDetailsComponent,
    SafeurlPipe,
    IncomingBookingComponent,
    TransactionHistoryComponent,
    RescheduleComponent,
    CancelAppointmentComponent,
    ConfirmationDialogComponent,
    CancelDialogComponent,
    UpdateEmailAddressComponent,
    PasswordDialogComponent
  ],
  entryComponents: [
    ThankYouComponent,
    ConfirmationDialogComponent,
    CancelDialogComponent,
    PasswordDialogComponent
  ],
  // entryComponents: [ConfirmationDialogComponent, ConfirmationDialogCommentComponent],
  imports: [
    BrowserModule,
    HttpClientModule,
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    BrowserModule,
    AppRoutingModule,
    ReactiveFormsModule,
    
    InputsModule,
    DateInputsModule,

    NgxSpinnerModule,
    MyDatePickerModule,

    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatToolbarModule,
    MatCardModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatMomentDateModule,
    MatCheckboxModule,
    MatRadioModule,
    MatSnackBarModule,
    MatButtonModule,
    MatDividerModule,
    MatListModule,
    MatIconModule,
    MatSlideToggleModule,
    ScrollingModule,
    MatSortModule,
    MatTableModule,
    MatPaginatorModule,
    MatAutocompleteModule,
    MatMenuModule,

    ToastrModule.forRoot(),
    ModalModule.forRoot(),
    BsDatepickerModule.forRoot()
  ],
  providers: [
    FormBuilder,
    StorageService,
    WebBookingService,
    WebGiftVoucherService,
    NavigatorService,
    DatePipe,
    VisitorGuard,
    StringService,
    CustomerGuard,
    DateService,
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [ConfigService],
      useFactory: (appConfigService: ConfigService) => {
        return () => {
          return appConfigService.load();
        };
      }
    },
    {
      provide: LOCALE_ID,
      deps: [ConfigService],
      useFactory: (appConfigService: ConfigService) => appConfigService.config.locale
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
